import { PayloadAction } from '@reduxjs/toolkit';
import {
	AppearanceOverlayConfig,
	Gender,
	IPedFaceMorphValue,
	IPedOverlayValue,
	IPedParentsValue,
} from '~/shared';
import { createSlice } from '../wrappers';

interface CharacterCreationSlice {
	tabIndex: number;
	maxTabIndex: number;
	gender: Gender;
	overlayConfigs: AppearanceOverlayConfig[];
	parentsValue: IPedParentsValue;
	faceMorphValues: IPedFaceMorphValue[];
	overlayValues: IPedOverlayValue[];
	charCreationModalVisible: boolean;
	inputActionsLocked: boolean;
	firstName: string;
	lastName: string;
}

const initialState: CharacterCreationSlice = {
	tabIndex: 0,
	maxTabIndex: 3,
	gender: Gender.MALE,
	overlayConfigs: [],
	parentsValue: {
		momIndex: 21,
		dadIndex: 1,
		faceBlend: 0,
		skinBlend: 0,
	},
	faceMorphValues: [],
	overlayValues: [],
	charCreationModalVisible: false,
	inputActionsLocked: false,
	firstName: '',
	lastName: '',
};

const characterCreationSlice = createSlice({
	name: 'characterCreation',
	initialState,
	reducers: {
		setTabIndexPrev: (state) => {
			if (state.charCreationModalVisible) {
				return;
			}

			const index = state.tabIndex;
			state.tabIndex = index > 0 ? index - 1 : index;
		},
		setTabIndexNext: (state) => {
			if (state.charCreationModalVisible) {
				return;
			}

			const index = state.tabIndex;
			const maxIndex = state.maxTabIndex;
			state.tabIndex = index < maxIndex ? index + 1 : index;
		},
		setTabIndex: (state, action) => {
			state.tabIndex = action.payload;
		},
		setMaxTabIndex: (state, action) => {
			state.maxTabIndex = action.payload;
		},
		setGender: (state, action) => {
			state.gender = action.payload;
		},
		setParentsValue: (state, action) => {
			state.parentsValue = action.payload;
		},
		setFaceMorphValues: (state, action) => {
			state.faceMorphValues = action.payload;
		},
		setFaceMorphValue: (
			state,
			action: PayloadAction<{ index: number; value: number }>,
		) => {
			const { index, value } = action.payload;
			const valueIndex = state.faceMorphValues.findIndex(
				(element) => element.index === index,
			);

			const faceMorphValues = [...state.faceMorphValues];

			if (valueIndex !== -1) {
				state.faceMorphValues = faceMorphValues.map((item, i) =>
					i === valueIndex ? { ...item, value } : item,
				);
			}
		},
		setOverlayConfigs: (state, action) => {
			state.overlayConfigs = action.payload;
		},
		setOverlayValues: (state, action) => {
			state.overlayValues = action.payload;
		},
		showCharacterCreationModal: (state) => {
			state.charCreationModalVisible = true;
		},
		hideCharacterCreationModal: (state) => {
			state.charCreationModalVisible = false;
		},
		setInputActionsLocked: (state, action) => {
			state.inputActionsLocked = action.payload;
		},
		setFirstName: (state, action) => {
			state.firstName = action.payload;
		},
		setLastName: (state, action) => {
			state.lastName = action.payload;
		},
	},
});

export const {
	setTabIndexPrev,
	setTabIndexNext,
	setTabIndex,
	setGender,
	setParentsValue,
	setFaceMorphValues,
	setFaceMorphValue,
	setOverlayConfigs,
	setOverlayValues,
	setMaxTabIndex,
	setInputActionsLocked,
	setFirstName,
	setLastName,
} = characterCreationSlice.actions;

export default characterCreationSlice.reducer;
